import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import routes from 'routes'
import { IAthleteProfile, ICoachProfile } from 'API/profile'
import { ActiveProfileType } from 'configs/constants/enums/auth'
import { useStores } from 'hooks/useStores'
import { PlayProfileRole, PlayProfileSummary } from 'pages/PlayDashboardPage/mock-data'
import { MembershipInfo } from 'types/auth'

// Adapter layer: bridges the BE profile-centric shape (athleteProfiles[] +
// coachProfile) to the existing PlayProfileSummary used by ProfileSwitcher /
// BottomNav / PlayProfilePage. Consumers must be observer-wrapped (mobx-react)
// to react to profileStore changes.

const AVATAR_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#ef4444', '#0ea5e9']

const ATHLETE_PREFIX = 'athlete-'
const COACH_PREFIX = 'coach-'

const initialsFromName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const summaryFromAthlete = (p: IAthleteProfile, idx: number): PlayProfileSummary => {
  const name = p.fullName?.trim() || p.nickname?.trim() || 'Athlete'
  const role: PlayProfileRole = p.isManagedByGuardian ? 'child' : 'player'
  return {
    id: `${ATHLETE_PREFIX}${p.id}`,
    name,
    role,
    avatarColor: AVATAR_COLORS[idx % AVATAR_COLORS.length],
    initials: initialsFromName(name),
    avatarUrl: p.avatarUrl ?? undefined,
    subline: role === 'child' ? 'Youth Profile' : 'Player Profile'
  }
}

const summaryFromCoach = (c: ICoachProfile): PlayProfileSummary => {
  const name = c.displayName?.trim() || 'Coach'
  return {
    id: `${COACH_PREFIX}${c.id}`,
    name,
    role: 'coach',
    avatarColor: '#10b981',
    initials: initialsFromName(name),
    avatarUrl: c.avatarUrl ?? undefined,
    subline: 'Coach Profile'
  }
}

export const useActivePlayProfile = () => {
  const { profileStore, authStore } = useStores()
  const navigate = useNavigate()

  useEffect(() => {
    // Idempotent fetch on first mount. The store's internal isLoading flag
    // prevents overlapping requests; re-mounts during dev refetch which is
    // fine and bounded.
    profileStore.loadMyProfiles().catch(() => {
      // Errors are surfaced via profileStore.error; non-fatal here.
    })
  }, [profileStore])

  const profiles: PlayProfileSummary[] = useMemo(() => {
    const athleteSummaries = profileStore.athleteProfiles.map(summaryFromAthlete)
    const coachSummary = profileStore.coachProfile ? [summaryFromCoach(profileStore.coachProfile)] : []
    // Display order: player -> coach -> child (youth always last so users see
    // their primary identities first in switchers/dropdowns).
    const ORDER: Record<PlayProfileRole, number> = { player: 0, coach: 1, child: 2 }
    return [...athleteSummaries, ...coachSummary].sort((a, b) => ORDER[a.role] - ORDER[b.role])
  }, [profileStore.athleteProfiles, profileStore.coachProfile])

  const active: PlayProfileSummary | undefined = useMemo(() => {
    if (profileStore.activeProfile) {
      const prefix = profileStore.activeProfile.type === ActiveProfileType.ATHLETE ? ATHLETE_PREFIX : COACH_PREFIX
      const targetId = `${prefix}${profileStore.activeProfile.id}`
      const match = profiles.find(p => p.id === targetId)
      if (match) return match
    }
    return profiles[0]
  }, [profileStore.activeProfile, profiles])

  const setActive = (p: PlayProfileSummary): void => {
    // Capture the prior selection so we only refetch profile-scoped data when
    // the active profile actually changes. Re-selecting the same profile is a
    // no-op so we avoid an unnecessary memberships round-trip.
    const prior = profileStore.activeProfile
    const applyAndRefreshIfChanged = (next: { id: number; type: ActiveProfileType }) => {
      // Persist the selection first so the X-Active-Profile-* headers (read from
      // localStorage by the API interceptor) are in place before authStore
      // refetches memberships under the new profile.
      profileStore.setActiveProfile(next)
      const changed = !prior || prior.id !== next.id || prior.type !== next.type
      if (!changed) return
      // Land the user on the dashboard so a page scoped to the prior profile
      // type doesn't stay open after switching (page access isn't gated per type).
      navigate(routes.playDashboard.value)
      // Delegate the side-effect chain to authStore so it owns membership
      // refetch AND downstream org-scoped store cleanup in one place (DRY).
      // Fire-and-forget keeps setActive synchronous; failures surface through
      // authStore and the persisted selection lets the user retry by switching.
      const switchPromise =
        next.type === ActiveProfileType.COACH
          ? authStore.switchToCoachProfile(next.id)
          : authStore.switchToAthleteProfile(next.id)
      switchPromise.catch(() => {
        // Non-fatal — selection already persisted; user can re-switch to retry.
      })
    }

    if (p.id.startsWith(ATHLETE_PREFIX)) {
      const id = Number(p.id.slice(ATHLETE_PREFIX.length))
      if (Number.isFinite(id) && id > 0) {
        applyAndRefreshIfChanged({ id, type: ActiveProfileType.ATHLETE })
      }
      return
    }
    if (p.id.startsWith(COACH_PREFIX)) {
      const id = Number(p.id.slice(COACH_PREFIX.length))
      if (Number.isFinite(id) && id > 0) {
        applyAndRefreshIfChanged({ id, type: ActiveProfileType.COACH })
      }
    }
  }

  const raw = {
    athleteProfiles: profileStore.athleteProfiles,
    coachProfile: profileStore.coachProfile
  }

  /** Current org membership — single source of truth for role-gated UI (e.g. Invite button). */
  const currentMembership: MembershipInfo | null = authStore.currentMembership

  return { profiles, active, setActive, raw, currentMembership }
}
